/**
 * Verifizierungs-Link ungültig/abgelaufen – /email-verifizierung  (NT-REG-FUNNEL-01, Teil D)
 *
 * Ziel des Redirects aus GET /api/v1/auth/verify-email, wenn der Token fehlt, ungültig
 * oder bereits verwendet ist (?status=invalid) oder abgelaufen ist (?status=expired).
 * Kein 404, kein rohes JSON — erklärender Text plus Formular für einen neuen Link.
 */

import { resendVerification } from '../services/auth.js';
import { ApiError } from '../services/api.js';

// Muss mit TOKEN_EXPIRY_HOURS in auth.service.ts übereinstimmen (kein Shared-Import
// für reine Anzeige-Werte — etabliertes Repo-Muster).
const VERIFY_LINK_VALID_HOURS = 24;

type LinkStatus = 'invalid' | 'expired';

export function renderEmailVerifizierung(): void {
  const app = document.getElementById('app');
  if (!app) return;

  const params = new URLSearchParams(window.location.search);
  const rawStatus = params.get('status');
  const status: LinkStatus = rawStatus === 'expired' ? 'expired' : 'invalid';

  const heading = status === 'expired' ? 'Link abgelaufen' : 'Link ungültig';
  const explanation =
    status === 'expired'
      ? `Dieser Bestätigungslink war nur ${String(VERIFY_LINK_VALID_HOURS)} Stunden gültig und ist mittlerweile abgelaufen.`
      : 'Dieser Bestätigungslink ist ungültig oder wurde bereits verwendet.';

  app.innerHTML = `
    <main class="auth-page">
      <div class="auth-card">
        <p class="auth-brand">&#9654; NIGHTTRADE</p>
        <h1 class="auth-title">${heading}</h1>
        <p class="auth-subtitle">${explanation} Fordere einfach einen neuen Link an.</p>

        <form id="ev-form" novalidate>
          <div class="form-group">
            <label for="ev-email">E-Mail-Adresse</label>
            <input
              type="email"
              id="ev-email"
              name="email"
              autocomplete="email"
              required
              placeholder="spieler@beispiel.de"
            />
          </div>

          <div
            id="ev-error"
            class="form-error"
            role="alert"
            aria-live="polite"
            hidden
          ></div>

          <div
            id="ev-success"
            class="form-success"
            role="status"
            aria-live="polite"
            hidden
          ></div>

          <button type="submit" id="ev-submit" class="primary btn-full">
            Neuen Link anfordern
          </button>
        </form>

        <nav class="auth-links">
          <a href="/login">&#8592; Zurück zum Login</a>
        </nav>
      </div>
    </main>
  `;

  const form = document.getElementById('ev-form') as HTMLFormElement | null;
  const emailInput = document.getElementById('ev-email') as HTMLInputElement | null;
  const submitBtn = document.getElementById('ev-submit') as HTMLButtonElement | null;
  const errorDiv = document.getElementById('ev-error') as HTMLDivElement | null;
  const successDiv = document.getElementById('ev-success') as HTMLDivElement | null;

  if (!form || !emailInput || !submitBtn || !errorDiv || !successDiv) return;

  function showError(message: string): void {
    if (!errorDiv) return;
    errorDiv.textContent = message;
    errorDiv.hidden = false;
    if (successDiv) successDiv.hidden = true;
  }

  function showSuccess(message: string): void {
    if (!successDiv) return;
    successDiv.textContent = message;
    successDiv.hidden = false;
    if (errorDiv) errorDiv.hidden = true;
    if (form) form.hidden = true;
  }

  function setLoading(loading: boolean): void {
    if (!submitBtn) return;
    submitBtn.disabled = loading;
    submitBtn.textContent = loading ? '■ WIRD GESENDET...' : 'Neuen Link anfordern';
    submitBtn.setAttribute('aria-busy', String(loading));
  }

  form.addEventListener('submit', (event: Event): void => {
    event.preventDefault();
    errorDiv.hidden = true;

    const email = emailInput.value.trim();
    if (!email) {
      showError('Bitte E-Mail-Adresse eingeben.');
      return;
    }

    setLoading(true);

    void (async (): Promise<void> => {
      try {
        await resendVerification(email);
        // Immer gleiche Meldung – Backend gibt ebenfalls neutrale Response zurück
        showSuccess('Falls für diese E-Mail noch eine Bestätigung aussteht, wurde ein neuer Link versendet. Bitte prüfe dein Postfach — auch den Spam-Ordner.');
      } catch (err: unknown) {
        if (err instanceof ApiError && err.status === 429) {
          showError('Zu viele Versuche. Bitte kurz warten.');
        } else {
          // Auch bei sonstigem Fehler: neutrale Meldung (verhindert User-Enumeration)
          showSuccess('Falls für diese E-Mail noch eine Bestätigung aussteht, wurde ein neuer Link versendet. Bitte prüfe dein Postfach — auch den Spam-Ordner.');
        }
      } finally {
        setLoading(false);
      }
    })();
  });
}
